import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox, Icon, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { getMetaConversationName } from 'app/utils/conversation';
import { ConversationDetails, ConversationDetailsLayout, } from 'components/conversationsOverview/components/ConversationDetails';
import { useCommandHandler } from 'utils/commands';
import { useConversationHasMissingRequiredCustomFieldsInAcw } from 'utils/conversation';
import { createAppUrl } from 'utils/url/createAppUrl';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import styles from './conversationListItem.module.scss';
export const ConversationListItem = ({ listIndex, conversationsLastSearchParams, isViewingConversation, metaConversation, isMenuOpen, typing, conversationsRemove, wsRequest, onToggleNavigation, }) => {
    var _a;
    const navigate = useNavigate();
    const { conversation, isAssignedToMe } = metaConversation;
    const csid = (_a = conversation.id) !== null && _a !== void 0 ? _a : 0;
    const hasMissingRequiredCustomFieldsInAcw = useConversationHasMissingRequiredCustomFieldsInAcw({ csid });
    const removeConversation = useCallback(() => {
        conversationsRemove(csid);
        wsRequest({
            type: 'connection:unsubscribe',
            params: {
                type: 'conversation',
                csid,
            },
        });
        // Check if we are closing the conversation we are looking at
        if (isViewingConversation) {
            navigate(createAppUrl({
                path: '/conversations',
                params: { search: conversationsLastSearchParams },
            }));
        }
    }, [
        conversationsRemove,
        csid,
        wsRequest,
        isViewingConversation,
        navigate,
        conversationsLastSearchParams,
    ]);
    const extraContent = useMemo(() => {
        const isClosed = conversation.is_closed && conversation.status === 'closed';
        const isPending = conversation.status === 'pending';
        if (isClosed && hasMissingRequiredCustomFieldsInAcw) {
            return (_jsx(Tooltip, Object.assign({ wrap: "span", wrapProps: { className: styles.tooltipWrapper }, content: "Missing required attributes", dark: true }, { children: _jsx(Icon, { className: styles.warningIcon, name: "exclamation-triangle-filled" }) })));
        }
        if (isClosed || isPending || !isAssignedToMe) {
            return (_jsx(Tooltip, Object.assign({ wrap: "span", wrapProps: { className: styles.tooltipWrapper }, content: "Remove", dark: true }, { children: _jsx("button", Object.assign({ type: "button", "aria-label": "Remove conversation", className: styles.closeConversationButton, onClick: () => removeConversation() }, { children: _jsx(Icon, { name: "close-circle-filled" }) })) })));
        }
        return null;
    }, [
        conversation.is_closed,
        conversation.status,
        hasMissingRequiredCustomFieldsInAcw,
        isAssignedToMe,
        removeConversation,
    ]);
    const detailsLayout = isMenuOpen
        ? ConversationDetailsLayout.Sidebar
        : ConversationDetailsLayout.SidebarCollapsed;
    const rootClass = classnames({
        [styles.root]: true,
        [styles.rootCollapsed]: !isMenuOpen,
        [styles.rootHasExtra]: !!extraContent,
    });
    const isInFirst9 = listIndex >= 0 || listIndex < 9;
    return (_jsxs("li", Object.assign({ className: rootClass }, { children: [isInFirst9 && (_jsx(ConversationListItemCommandHandler, { index: listIndex + 1, csid: csid })), _jsx(NavLink, Object.assign({ className: ({ isActive }) => classnames(styles.link, { [styles.linkActive]: isActive }), to: `/conversation/${conversation.id}`, onClick: () => onToggleNavigation === null || onToggleNavigation === void 0 ? void 0 : onToggleNavigation() }, { children: _jsx(ConversationDetails, { metaConversation: metaConversation, conversationName: getMetaConversationName(metaConversation), layout: detailsLayout, isTyping: (typing === null || typing === void 0 ? void 0 : typing.csid) === conversation.id, newMessageCount: metaConversation.unread }) })), extraContent && (_jsx(FlexBox, Object.assign({ className: styles.extra }, { children: extraContent })))] })));
};
/**
 * Represents the component for navigating to an active conversation with a
 * command.
 */
const ConversationListItemCommandHandler = ({ index, csid, }) => {
    const navigate = useAppNavigate();
    const name = `conversation:goToActiveConversation${index}`;
    useCommandHandler({
        name,
        actionFn: () => {
            navigate({
                path: '/conversation/:csid',
                params: { path: { csid: csid.toString() } },
            });
        },
    });
    return null;
};
